import { config } from '@/data/config';
import { request } from '@/data/http';
import type { Papel } from '@/data/types';

/**
 * Cliente da API `admin` — capabilities e estado do PAN por instalação.
 * Separado de `PanDataSource` de propósito: são ações administrativas
 * restritas por capability, sem sentido em modo demo (não há "simular
 * conceder capability" sem um Keycloak de verdade por trás).
 */

export interface UsuarioKeycloak {
  keycloakUserId: string;
  email: string;
  username: string;
  nome: string;
}

export interface CapabilitiesDoUsuario {
  keycloakUserId: string;
  email: string;
  capabilities: string[];
}

export interface UsuarioComCapabilities extends UsuarioKeycloak {
  capabilities: string[];
}

/** Uma "instalação" no sentido de infraestrutura — 1 por ambiente de nuvem
 * ATIVO (`pan.instalacoes_infra`), mantida por trigger em `nuvem.ambientes`.
 * Acesso real não depende de `pan.instalacoes` — o ambiente existir aqui já
 * basta. `ativo` é só o liga/desliga de agendamento. `id` é o id do
 * registro (não confundir com um tenant); use `tenantIds` pra saber quais
 * tenants reais ela reúne. `codigo`/`nome` são editáveis (apelido) — uma vez
 * editados manualmente (`codigoManual`), a sincronização automática para de
 * sobrescrever.
 * `papel`: uma relação controladora/controlada só conta quando aponta pra
 * FORA deste ambiente — dois tenants do MESMO ambiente já são a mesma
 * instalação (ex.: Pramex), não faz sentido um ser "controladora" do outro. */
export interface InstalacaoEstado {
  id: number;
  codigo: string;
  nome: string;
  codigoManual: boolean;
  ativo: boolean;
  beta: boolean;
  /** Trava global — só sysadmin liga: some do hub, não agenda e não personifica. */
  bloqueada: boolean;
  /** Algum tenant do ambiente é cliente ANA (`diretorio.tenants.clienteana`). */
  ana: boolean;
  /** Cliente interno da Nasajon (`diretorio.clientes.interno`). */
  interno: boolean;
  zona: string | null;
  /** As duas metades do nome da zona (ex.: "Sirius-Orion") correlacionadas
   * com o ativo real por trás — cluster de banco e servidor/fileserver. */
  zonaClusterNome: string | null;
  zonaClusterTecnico: string | null;
  zonaServidorNome: string | null;
  zonaServidorTecnico: string | null;
  versao: string | null;
  ambienteCodigo: string;
  tenantIds: number[];
  representanteTenantId: number;
  papel: Papel;
  controladoraId: number | null;
  controladoraCodigo: string | null;
}

export interface FiltrosInstalacao {
  zonas: string[];
  versoes: string[];
  /** Há alguma instalação ANA / de cliente interno no inventário. */
  temAna: boolean;
  temInterno: boolean;
}

/** Capacidade global de agendamento — quantas instalações cabem por
 * horário. Mudar aqui propaga na hora (lido ao vivo pelo backend, sem
 * cache) e zera qualquer retenção/marcação de horário em andamento. */
export interface AgendamentoConfig {
  maxSeats: number;
}

/** Uma linha do rastro de auditoria de uma instalação — `personificar`
 * (alguém assumiu acesso de administrador sobre ela), `habilitar` (toggle
 * de habilitada p/ agendamento) ou `beta` (toggle do opt-in de versão
 * beta). `detalhe` traz `{de, para}` nas duas últimas, vazio na primeira. */
export interface AuditoriaInstalacao {
  operacao: 'personificar' | 'habilitar' | 'beta' | 'bloquear';
  atorEmail: string;
  detalhe: Record<string, unknown>;
  criadoEm: string;
}

function linha<T>(raw: unknown): T {
  return raw as T;
}

export const adminDataSource = {
  async buscarUsuariosKeycloak(query: string, signal?: AbortSignal): Promise<UsuarioKeycloak[]> {
    const payload = await request<Array<Record<string, unknown>>>('/capabilities/buscar', {
      recurso: 'a busca de usuários',
      query: { query },
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return (payload ?? []).map((item) => ({
      keycloakUserId: String(item.keycloak_user_id ?? ''),
      email: String(item.email ?? ''),
      username: String(item.username ?? ''),
      nome: String(item.nome ?? ''),
    }));
  },

  async listarUsuariosComCapabilities(signal?: AbortSignal): Promise<UsuarioComCapabilities[]> {
    const payload = await request<Array<Record<string, unknown>>>('/capabilities', {
      recurso: 'a lista de usuários com capabilities',
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return (payload ?? []).map((item) => ({
      keycloakUserId: String(item.keycloak_user_id ?? ''),
      email: String(item.email ?? ''),
      username: String(item.username ?? ''),
      nome: String(item.nome ?? ''),
      capabilities: Array.isArray(item.capabilities) ? (item.capabilities as string[]) : [],
    }));
  },

  async obterCapabilities(keycloakUserId: string, signal?: AbortSignal): Promise<CapabilitiesDoUsuario> {
    const payload = await request<Record<string, unknown>>(`/capabilities/${keycloakUserId}`, {
      recurso: 'as capabilities do usuário',
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return {
      keycloakUserId: String(payload.keycloak_user_id ?? ''),
      email: String(payload.email ?? ''),
      capabilities: Array.isArray(payload.capabilities) ? (payload.capabilities as string[]) : [],
    };
  },

  async concederCapability(keycloakUserId: string, capability: string, signal?: AbortSignal): Promise<void> {
    await request<unknown>(`/capabilities/${keycloakUserId}`, {
      recurso: 'a concessão da capability',
      method: 'POST',
      body: { capability },
      baseUrl: config.adminBaseUrl,
      signal,
    });
  },

  async revogarCapability(keycloakUserId: string, capability: string, signal?: AbortSignal): Promise<void> {
    await request<unknown>(`/capabilities/${keycloakUserId}/${capability}`, {
      recurso: 'a revogação da capability',
      method: 'DELETE',
      baseUrl: config.adminBaseUrl,
      signal,
    });
  },

  async buscarInstalacoesEstado(
    filtro: {
      q?: string;
      zona?: string;
      versao?: string;
      ativo?: boolean;
      ana?: boolean;
      interno?: boolean;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<{ itens: InstalacaoEstado[]; total: number }> {
    const payload = await request<{ items?: Array<Record<string, unknown>>; total?: number }>(
      '/estado-instalacao/instalacoes',
      {
        recurso: 'a busca de instalações',
        query: {
          q: filtro.q,
          zona: filtro.zona,
          versao: filtro.versao,
          ativo: filtro.ativo,
          ana: filtro.ana,
          interno: filtro.interno,
          limit: filtro.limit,
          offset: filtro.offset,
        },
        baseUrl: config.adminBaseUrl,
        signal,
      },
    );
    return { itens: (payload.items ?? []).map((item) => toInstalacaoEstado(item)), total: Number(payload.total ?? 0) };
  },

  async filtrosInstalacao(signal?: AbortSignal): Promise<FiltrosInstalacao> {
    const payload = await request<Record<string, unknown>>('/estado-instalacao/filtros', {
      recurso: 'os filtros de instalação',
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return {
      zonas: Array.isArray(payload.zonas) ? (payload.zonas as string[]) : [],
      versoes: Array.isArray(payload.versoes) ? (payload.versoes as string[]) : [],
      temAna: payload.tem_ana === true,
      temInterno: payload.tem_interno === true,
    };
  },

  async editarInstalacao(
    instalacaoId: number,
    alteracoes: { ativo?: boolean; beta?: boolean; bloqueada?: boolean; codigo?: string; nome?: string },
    signal?: AbortSignal,
  ): Promise<InstalacaoEstado> {
    const payload = await request<Record<string, unknown>>(`/estado-instalacao/instalacoes/${instalacaoId}`, {
      recurso: 'a edição da instalação',
      method: 'PATCH',
      body: alteracoes,
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return toInstalacaoEstado(payload);
  },

  async editarInstalacoesEmLote(
    ids: number[],
    alteracoes: { ativo?: boolean; beta?: boolean },
    signal?: AbortSignal,
  ): Promise<{ atualizadas: number }> {
    const payload = await request<Record<string, unknown>>('/estado-instalacao/instalacoes', {
      recurso: 'a ação em massa nas instalações',
      method: 'PATCH',
      body: { ids, ...alteracoes },
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return { atualizadas: Number(payload.atualizadas ?? 0) };
  },

  async buscarAgendamentoConfig(signal?: AbortSignal): Promise<AgendamentoConfig> {
    const payload = await request<Record<string, unknown>>('/agendamento-config', {
      recurso: 'a configuração de agendamento',
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return { maxSeats: Number(payload.maxSeats ?? 0) };
  },

  async atualizarAgendamentoConfig(maxSeats: number, signal?: AbortSignal): Promise<AgendamentoConfig> {
    const payload = await request<Record<string, unknown>>('/agendamento-config', {
      recurso: 'a configuração de agendamento',
      method: 'PATCH',
      body: { maxSeats },
      baseUrl: config.adminBaseUrl,
      signal,
    });
    return { maxSeats: Number(payload.maxSeats ?? 0) };
  },

  async buscarAuditoriaInstalacao(
    instalacaoId: number,
    /** Sem `de`/`ate`, o backend já limita a janela a ~30 dias a partir de
     * hoje (nunca mais que 31) — mesma regra do Histórico. `operacao`:
     * 'personificar' | 'habilitar' | 'beta'. `ator`: trecho do e-mail. */
    filtro: {
      de?: string;
      ate?: string;
      operacao?: string;
      ator?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<{ itens: AuditoriaInstalacao[]; total: number }> {
    const payload = await request<{ items?: Array<Record<string, unknown>>; total?: number }>(
      `/estado-instalacao/instalacoes/${instalacaoId}/auditoria`,
      {
        recurso: 'a auditoria da instalação',
        query: {
          de: filtro.de,
          ate: filtro.ate,
          operacao: filtro.operacao,
          ator: filtro.ator,
          limit: filtro.limit,
          offset: filtro.offset,
        },
        baseUrl: config.adminBaseUrl,
        signal,
      },
    );
    const itens = (payload.items ?? []).map((item) => ({
      operacao: String(item.operacao ?? '') as AuditoriaInstalacao['operacao'],
      atorEmail: String(item.atorEmail ?? ''),
      detalhe: (item.detalhe as Record<string, unknown>) ?? {},
      criadoEm: String(item.criadoEm ?? ''),
    }));
    return { itens, total: Number(payload.total ?? 0) };
  },
};

function toInstalacaoEstado(raw: Record<string, unknown>): InstalacaoEstado {
  return linha<InstalacaoEstado>({
    id: Number(raw.id),
    codigo: String(raw.codigo ?? ''),
    nome: String(raw.nome ?? ''),
    codigoManual: Boolean(raw.codigoManual),
    ativo: Boolean(raw.ativo),
    beta: Boolean(raw.beta),
    bloqueada: Boolean(raw.bloqueada),
    ana: Boolean(raw.ana),
    interno: Boolean(raw.interno),
    zona: raw.zona != null ? String(raw.zona) : null,
    zonaClusterNome: raw.zonaClusterNome != null ? String(raw.zonaClusterNome) : null,
    zonaClusterTecnico: raw.zonaClusterTecnico != null ? String(raw.zonaClusterTecnico) : null,
    zonaServidorNome: raw.zonaServidorNome != null ? String(raw.zonaServidorNome) : null,
    zonaServidorTecnico: raw.zonaServidorTecnico != null ? String(raw.zonaServidorTecnico) : null,
    versao: raw.versao != null ? String(raw.versao) : null,
    ambienteCodigo: String(raw.ambienteCodigo ?? ''),
    tenantIds: Array.isArray(raw.tenantIds) ? (raw.tenantIds as unknown[]).map(Number) : [],
    representanteTenantId: Number(raw.representanteTenantId),
    papel: (raw.papel as Papel | undefined) ?? 'Instalação',
    controladoraId: raw.controladoraId != null ? Number(raw.controladoraId) : null,
    controladoraCodigo: raw.controladoraCodigo != null ? String(raw.controladoraCodigo) : null,
  });
}
